import React, {
  useState, useEffect, useMemo, memo, useCallback,
} from 'react'

import {
  classnames, ClosingDropdown, ChevronDown, ChevronUp, Icon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { node, oneOf, string, func, bool } from 'prop-types'

import { NOOP } from 'Utils/stableDefaults'
import useDelayLoad from 'Utils/useDelayLoad'

function FiltersWrapper({
  placeholder,
  positionHorizontal = 'right',
  content,
  onOpenChange = null,
  triggerCloseDropdown = false,
  setTriggerCloseDropdown = NOOP,
  prefixIcon = null,
  hasSelectedAnyFilters = false,
  closingDropdownClassName = '',
  contentClassName = '',
  size = 'large',
}) {
  const { isLoaded, elementRef } = useDelayLoad()
  const [isOpen, setIsOpen] = useState(false)
  // Triangle's ClosingDropdown only closes on an outside mousedown, so Escape left the panel and
  // its backdrop up. triggerCloseDropdown is its supported external close channel; keep a local
  // flag so this works whether or not the parent also drives that prop.
  const [closeOnEscape, setCloseOnEscape] = useState(false)

  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(isOpen)
    }
  }, [isOpen, onOpenChange])

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setCloseOnEscape(true)
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  const position = useMemo(() => ({ horizontal: positionHorizontal }), [positionHorizontal])

  const handleOpenChange = useCallback((open) => {
    setIsOpen(open)
  }, [])

  // ClosingDropdown resets the trigger to false once it has closed; clear both sources.
  const handleTriggerCloseChange = useCallback((value) => {
    setCloseOnEscape(value)
    setTriggerCloseDropdown(value)
  }, [setTriggerCloseDropdown])

  const className = useMemo(() => classnames(
    "cgt-text-input cgt-text-input--medium cgt-text-input--primary cgt-text-input--icon-trail filter-wrapper__trigger",
    hasSelectedAnyFilters && 'filter-wrapper__trigger--filled',
  ), [hasSelectedAnyFilters])

  return (
    <ClosingDropdown
      // '' avoids null so the required `content` propType is satisfied; falsy still renders nothing
      content={isLoaded ? content : ''}
      isSimpleContent
      isSpaced
      size={size}
      position={position}
      contentClassName={contentClassName}
      onOpenChange={handleOpenChange}
      triggerCloseDropdown={triggerCloseDropdown || closeOnEscape}
      setTriggerCloseDropdown={handleTriggerCloseChange}
      className={closingDropdownClassName}
      withBackdrop
    >
      <div
        ref={elementRef}
        className={className}
      >
        {prefixIcon}
        <div className="filter-wrapper__placeholder">
          {placeholder}
        </div>
        <Icon
          svgComponent={isOpen ? ChevronUp : ChevronDown}
          color="darker"
          notSpaced
          className="filter-wrapper__trail-icon"
        />
      </div>
    </ClosingDropdown>
  )
}

FiltersWrapper.propTypes = {
  placeholder: string.isRequired,
  positionHorizontal: oneOf(['left', 'right']),
  content: node.isRequired,
  onOpenChange: func,
  triggerCloseDropdown: bool,
  setTriggerCloseDropdown: func,
  prefixIcon: node,
  hasSelectedAnyFilters: bool,
  closingDropdownClassName: string,
  contentClassName: string,
  size: oneOf(['large', 'medium']),
}

export default memo(FiltersWrapper)
